import { AuthMethod } from './types';

// CRM Provider information
export interface CrmProvider {
  id: string;
  name: string;
  description: string;
  logo?: string;
  authMethod: AuthMethod | 'dual';
  baseUrl: string;
  apiVersion?: string;
  documentationUrl: string;
  features: string[];
  fieldMappings: {
    contacts?: Record<string, string>;
    companies?: Record<string, string>;
    opportunities?: Record<string, string>;
    activities?: Record<string, string>;
  };
  defaultSettings: {
    timeout: number;
    retryAttempts: number;
    retryDelay: number;
    headers?: Record<string, string>;
  };
  oauth2?: {
    authorizationUrl: string;
    tokenUrl: string;
    scope: string;
    defaultRedirectUri: string;
  };
  apiKey?: {
    header: string;
    queryParam?: string;
  };
}

// List of supported CRM providers
export const crmProviders: CrmProvider[] = [
  {
    id: 'redtail',
    name: 'Redtail CRM',
    description: 'The #1 CRM software for financial advisors',
    logo: 'https://www.redtailtechnology.com/wp-content/uploads/2021/06/redtail-logo.svg',
    authMethod: 'apikey',
    baseUrl: 'https://api.redtailtechnology.com/v3',
    documentationUrl: 'https://developers.redtailtechnology.com/docs',
    features: [
      'Contact Management',
      'Activity Tracking',
      'Calendar Integration',
      'Document Management',
      'Workflow Automation'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'FirstName',
        'last_name': 'LastName',
        'email': 'Email',
        'phone': 'Phone',
        'address': 'Address',
        'city': 'City',
        'state': 'State',
        'zip': 'Zip'
      },
      companies: {
        'name': 'CompanyName',
        'phone': 'CompanyPhone',
        'website': 'Website',
        'industry': 'Industry'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    apiKey: {
      header: 'X-API-KEY'
    }
  },
  {
    id: 'wealthbox',
    name: 'Wealthbox CRM',
    description: 'Modern CRM for financial advisors',
    logo: 'https://wealthbox.com/wp-content/uploads/2022/01/wealthbox-logo.svg',
    authMethod: 'dual',
    baseUrl: 'https://api.wealthbox.com/v1',
    documentationUrl: 'https://dev.wealthbox.com/',
    features: [
      'Contact Management',
      'Task Management',
      'Project Management',
      'Email Integration',
      'Custom Fields'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'first_name',
        'last_name': 'last_name',
        'email': 'email',
        'phone': 'phone',
        'address': 'address',
        'city': 'city',
        'state': 'state',
        'zip': 'zip'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://app.wealthbox.com/oauth/authorize',
      tokenUrl: 'https://app.wealthbox.com/oauth/token',
      scope: 'read write',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    },
    apiKey: {
      header: 'Authorization',
      queryParam: 'access_token'
    }
  },
  {
    id: 'salesforce',
    name: 'Salesforce Financial Services Cloud',
    description: 'Enterprise CRM for financial services',
    logo: 'https://www.salesforce.com/content/dam/web/en_us/www/images/sales-cloud/logo.png',
    authMethod: 'oauth2',
    baseUrl: 'https://login.salesforce.com/services/data/v56.0',
    apiVersion: 'v56.0',
    documentationUrl: 'https://developer.salesforce.com/docs',
    features: [
      'Contact Management',
      'Opportunity Management',
      'Task Management',
      'Reports & Dashboards',
      'Workflow Automation',
      'Custom Objects'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'FirstName',
        'last_name': 'LastName',
        'email': 'Email',
        'phone': 'Phone',
        'address': 'MailingStreet',
        'city': 'MailingCity',
        'state': 'MailingState',
        'zip': 'MailingPostalCode'
      },
      opportunities: {
        'name': 'Name',
        'amount': 'Amount',
        'stage': 'StageName',
        'close_date': 'CloseDate',
        'probability': 'Probability'
      }
    },
    defaultSettings: {
      timeout: 60000,
      retryAttempts: 3,
      retryDelay: 2000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://login.salesforce.com/services/oauth2/authorize',
      tokenUrl: 'https://login.salesforce.com/services/oauth2/token',
      scope: 'api refresh_token',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    }
  },
  {
    id: 'zoho',
    name: 'Zoho CRM for Finance',
    description: 'Affordable CRM for financial professionals',
    logo: 'https://www.zohowebstatic.com/sites/default/files/zoho_general_pages/zoho-logo-new.png',
    authMethod: 'oauth2',
    baseUrl: 'https://www.zohoapis.com/crm/v3',
    apiVersion: 'v3',
    documentationUrl: 'https://www.zoho.com/crm/developer/docs/api/v3/',
    features: [
      'Contact Management',
      'Deal Management',
      'Task Management',
      'Email Integration',
      'Workflow Automation'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'First_Name',
        'last_name': 'Last_Name',
        'email': 'Email',
        'phone': 'Phone',
        'address': 'Mailing_Street',
        'city': 'Mailing_City',
        'state': 'Mailing_State',
        'zip': 'Mailing_Zip'
      },
      opportunities: {
        'name': 'Deal_Name',
        'amount': 'Amount',
        'stage': 'Stage',
        'close_date': 'Closing_Date',
        'probability': 'Probability'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://accounts.zoho.com/oauth/v2/auth',
      tokenUrl: 'https://accounts.zoho.com/oauth/v2/token',
      scope: 'ZohoCRM.modules.ALL',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    }
  },
  {
    id: 'dynamics',
    name: 'Microsoft Dynamics 365',
    description: 'Microsoft\'s enterprise CRM solution',
    logo: 'https://dynamics.microsoft.com/content/dam/dynamics/images/dynamics-logo.svg',
    authMethod: 'oauth2',
    baseUrl: 'https://api.dynamics.com/v9.0',
    apiVersion: 'v9.0',
    documentationUrl: 'https://docs.microsoft.com/en-us/dynamics365/customerengagement/on-premises/developer/use-microsoft-dynamics-365-web-api',
    features: [
      'Contact Management',
      'Opportunity Management',
      'Task Management',
      'Reports & Dashboards',
      'Workflow Automation',
      'Microsoft Integration'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'firstname',
        'last_name': 'lastname',
        'email': 'emailaddress1',
        'phone': 'telephone1',
        'address': 'address1_line1',
        'city': 'address1_city',
        'state': 'address1_stateorprovince',
        'zip': 'address1_postalcode'
      },
      opportunities: {
        'name': 'name',
        'amount': 'estimatedvalue',
        'stage': 'stepname',
        'close_date': 'estimatedclosedate',
        'probability': 'closeprobability'
      }
    },
    defaultSettings: {
      timeout: 60000,
      retryAttempts: 3,
      retryDelay: 2000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://login.microsoftonline.com/common/oauth2/authorize',
      tokenUrl: 'https://login.microsoftonline.com/common/oauth2/token',
      scope: 'https://api.dynamics.com/user_impersonation',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    }
  },
  {
    id: 'hubspot',
    name: 'HubSpot CRM',
    description: 'Inbound marketing and sales CRM',
    logo: 'https://www.hubspot.com/hubfs/assets/hubspot.com/style-guide/brand-guidelines/guidelines_the-logo.svg',
    authMethod: 'dual',
    baseUrl: 'https://api.hubapi.com',
    documentationUrl: 'https://developers.hubspot.com/docs/api/overview',
    features: [
      'Contact Management',
      'Deal Management',
      'Task Management',
      'Email Integration',
      'Marketing Automation'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'firstname',
        'last_name': 'lastname',
        'email': 'email',
        'phone': 'phone',
        'address': 'address',
        'city': 'city',
        'state': 'state',
        'zip': 'zip'
      },
      companies: {
        'name': 'name',
        'phone': 'phone',
        'website': 'website',
        'industry': 'industry'
      },
      opportunities: {
        'name': 'dealname',
        'amount': 'amount',
        'stage': 'dealstage',
        'close_date': 'closedate'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://app.hubspot.com/oauth/authorize',
      tokenUrl: 'https://api.hubapi.com/oauth/v1/token',
      scope: 'contacts',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    },
    apiKey: {
      header: 'hapikey',
      queryParam: 'hapikey'
    }
  },
  {
    id: 'junxure',
    name: 'AdvisorEngine (Junxure)',
    description: 'CRM built for financial advisors',
    logo: 'https://www.advisorengine.com/wp-content/uploads/2022/01/advisorengine-logo.svg',
    authMethod: 'apikey',
    baseUrl: 'https://api.junxure.com/v2',
    documentationUrl: 'https://developer.junxure.com/docs',
    features: [
      'Contact Management',
      'Action Management',
      'Document Management',
      'Workflow Automation',
      'Client Portal'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'FirstName',
        'last_name': 'LastName',
        'email': 'Email',
        'phone': 'Phone',
        'address': 'Address',
        'city': 'City',
        'state': 'State',
        'zip': 'Zip'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    apiKey: {
      header: 'X-API-KEY'
    }
  },
  {
    id: 'practifi',
    name: 'Practifi',
    description: 'Business management platform for financial advice',
    logo: 'https://www.practifi.com/wp-content/uploads/2022/01/practifi-logo.svg',
    authMethod: 'oauth2',
    baseUrl: 'https://api.practifi.com/v1',
    documentationUrl: 'https://developer.practifi.com',
    features: [
      'Client Management',
      'Team Collaboration',
      'Workflow Automation',
      'Business Intelligence',
      'Compliance Management'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'FirstName',
        'last_name': 'LastName',
        'email': 'Email',
        'phone': 'Phone',
        'address': 'MailingStreet',
        'city': 'MailingCity',
        'state': 'MailingState',
        'zip': 'MailingPostalCode'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://login.practifi.com/oauth/authorize',
      tokenUrl: 'https://login.practifi.com/oauth/token',
      scope: 'api',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    }
  },
  {
    id: 'uptick',
    name: 'Uptick CRM',
    description: 'Simple CRM for financial advisors',
    logo: 'https://www.uptickhq.com/wp-content/uploads/2022/01/uptick-logo.svg',
    authMethod: 'apikey',
    baseUrl: 'https://api.uptickhq.com/v1',
    documentationUrl: 'https://developer.uptickhq.com',
    features: [
      'Contact Management',
      'Task Management',
      'Email Integration',
      'Calendar Sync',
      'Mobile App'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'first_name',
        'last_name': 'last_name',
        'email': 'email',
        'phone': 'phone',
        'address': 'address',
        'city': 'city',
        'state': 'state',
        'zip': 'zip'
      }
    },
    defaultSettings: {
      timeout: 30000,
      retryAttempts: 3,
      retryDelay: 1000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    apiKey: {
      header: 'X-API-KEY'
    }
  },
  {
    id: 'nexj',
    name: 'NexJ CRM',
    description: 'Enterprise CRM for wealth management',
    logo: 'https://www.nexj.com/wp-content/uploads/2022/01/nexj-logo.svg',
    authMethod: 'oauth2',
    baseUrl: 'https://api.nexj.com/v1',
    documentationUrl: 'https://developer.nexj.com',
    features: [
      'Client Management',
      'Household Management',
      'Interaction Management',
      'Workflow Automation',
      'Analytics & Reporting'
    ],
    fieldMappings: {
      contacts: {
        'first_name': 'firstName',
        'last_name': 'lastName',
        'email': 'emailAddress',
        'phone': 'phoneNumber',
        'address': 'streetAddress',
        'city': 'city',
        'state': 'stateProvince',
        'zip': 'postalCode'
      }
    },
    defaultSettings: {
      timeout: 60000,
      retryAttempts: 3,
      retryDelay: 2000,
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    },
    oauth2: {
      authorizationUrl: 'https://auth.nexj.com/oauth/authorize',
      tokenUrl: 'https://auth.nexj.com/oauth/token',
      scope: 'read write',
      defaultRedirectUri: `${window.location.origin}/auth/callback`
    }
  }
];

/**
 * Get a CRM provider by ID
 */
export function getCrmProvider(providerId: string): CrmProvider | undefined {
  return crmProviders.find(provider => provider.id === providerId);
}

/**
 * Get default connection configuration for a provider
 */
export function getDefaultConnectionConfig(providerId: string): Partial<any> {
  const provider = getCrmProvider(providerId);
  if (!provider) return {};
  
  const config: any = {
    name: provider.name,
    provider: providerId,
    url: provider.baseUrl,
    authMethod: provider.authMethod === 'dual' ? 'oauth2' : provider.authMethod,
    enabled: true,
    timeout: provider.defaultSettings.timeout,
    retryAttempts: provider.defaultSettings.retryAttempts,
    retryDelay: provider.defaultSettings.retryDelay,
    headers: provider.defaultSettings.headers
  };
  
  // Add auth method specific properties
  if (provider.authMethod === 'oauth2' || provider.authMethod === 'dual') {
    if (provider.oauth2) {
      config.authorizationUrl = provider.oauth2.authorizationUrl;
      config.tokenUrl = provider.oauth2.tokenUrl;
      config.scope = provider.oauth2.scope;
      config.redirectUri = provider.oauth2.defaultRedirectUri;
    }
  }
  
  if (provider.authMethod === 'apikey' || provider.authMethod === 'dual') {
    if (provider.apiKey) {
      config.apiKeyHeader = provider.apiKey.header;
      config.apiKeyQueryParam = provider.apiKey.queryParam;
    }
  }
  
  return config;
}